import { inject, Service } from '@angular/core';
import {
  asyncScheduler,
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  mergeWith,
  Observable,
  observeOn,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { ResourceType } from '@models';
import { Place, PlacesService } from './places.service';

type Normalizer = (value: string) => string;

type SelectionFilter<T> = (
  item: T,
  context: { normalizedTerm: string; term: string }
) => boolean;

type LocationFilter<T> = SelectionFilter<T>;

type LocationMapper<T> = (prediction: Place, context: { term: string }) => T;

type TermItemFactory<T> = (term: string) => T;

type DistinctComparer = (prev: string, current: string) => boolean;

export interface SelectionTypeaheadOptions<T> {
  getItems: () => readonly T[];
  getSearchValue: (item: T) => string | undefined;
  filters?: SelectionFilter<T>[];
  focus$?: Observable<string>;
  sortComparer?: (a: T, b: T) => number;
  debounceMs?: number;
  distinctComparer?: DistinctComparer;
  termNormalizer?: Normalizer;
}

export interface LocationTypeaheadOptions<T> {
  filter?: LocationFilter<T>;
  onLoadingChange?: (isLoading: boolean) => void;
  mapPrediction?: LocationMapper<T>;
  createTermItem?: TermItemFactory<T>;
  debounceMs?: number;
  distinctComparer?: DistinctComparer;
  termNormalizer?: Normalizer;
}

const defaultNormalizer: Normalizer = (value) =>
  (value ?? '').toLocaleLowerCase().trim();

export const VALID_INVITEE_TYPES = [
  ResourceType.USER,
  ResourceType.GROUP,
] as const;

export const MIN_PERMISSION = 0;

const defaultSortByDisplayName = <T extends { displayName?: string }>(
  a: T,
  b: T
) => {
  const left = defaultNormalizer(a.displayName ?? '');
  const right = defaultNormalizer(b.displayName ?? '');

  return left < right ? -1 : left > right ? 1 : 0;
};

const identityFilter: SelectionFilter<unknown> = () => true;

const defaultLocationMapper: LocationMapper<Place> = (prediction) => prediction;

const defaultTermItemFactory: TermItemFactory<Place> = (term) => ({
  description: term,
  id: term,
});

const defaultLocationFilter: LocationFilter<Place> = (item, context) =>
  defaultNormalizer(item.description).includes(context.normalizedTerm);

@Service()
export class TypeaheadService {
  placesService = inject(PlacesService);

  createSelectionTypeahead = <T>({
    getItems,
    getSearchValue,
    filters = [identityFilter],
    focus$,
    sortComparer,
    debounceMs = 300,
    distinctComparer,
    termNormalizer = defaultNormalizer,
  }: SelectionTypeaheadOptions<T>) => {
    const appliedFilters = filters.length ? filters : [identityFilter];
    const comparer = sortComparer ?? defaultSortByDisplayName;

    return (text$: Observable<string>) => {
      const typed$ = text$.pipe(
        debounceTime(debounceMs),
        distinctUntilChanged(distinctComparer)
      );
      const source$ = focus$
        ? typed$.pipe(mergeWith(focus$.pipe(debounceTime(debounceMs))))
        : typed$;

      return source$.pipe(
        map((term) => {
          const normalizedTerm = termNormalizer(term ?? '');
          const context = { normalizedTerm, term };
          const items = [...getItems()].sort(
            comparer as (a: T, b: T) => number
          );

          return items.filter((item) => {
            if (appliedFilters.some((filter) => !filter(item, context))) {
              return false;
            }

            const value = getSearchValue(item);

            if (!value) {
              return false;
            }

            const normalizedValue = termNormalizer(value);

            return normalizedValue.includes(normalizedTerm);
          });
        }),
        observeOn(asyncScheduler)
      );
    };
  };

  createLocationTypeahead = <T = Place>({
    filter = defaultLocationFilter as unknown as LocationFilter<T>,
    onLoadingChange,
    mapPrediction = defaultLocationMapper as unknown as LocationMapper<T>,
    createTermItem = defaultTermItemFactory as unknown as TermItemFactory<T>,
    debounceMs = 300,
    distinctComparer,
    termNormalizer = defaultNormalizer,
  }: LocationTypeaheadOptions<T> = {}) => {
    return (text$: Observable<string>) =>
      text$.pipe(
        tap(() => onLoadingChange?.(true)),
        debounceTime(debounceMs),
        distinctUntilChanged(distinctComparer),
        switchMap((term) => {
          const normalizedTerm = termNormalizer(term ?? '');
          const context = { normalizedTerm, term };

          return this.placesService.searchPlaces(term).pipe(
            take(1),
            map((response) => [
              createTermItem(term),
              ...response.predictions.map((prediction) =>
                mapPrediction(prediction, { term })
              ),
            ]),
            map((suggestions) =>
              suggestions.filter((item) => filter(item, context))
            ),
            catchError(() => of([] as T[])),
            finalize(() => onLoadingChange?.(false))
          );
        })
      );
  };
}
